import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  RefreshCcw,
  Save,
} from "lucide-react";
import { toast } from "react-toastify";

import {
  createOnPremOnboarding,
  getOnPremErrorMessage,
  getOnPremValidationErrors,
  getOnPremOnboarding,
  updateOnPremClient,
  updateOnPremFirstAdmin,
} from "../../apiOnPremAdmin";
import { getPasswordValidationState } from "../../utils/passwordPolicy";
import {
  getRuntimeMode,
  getProviderLabel,
  profileToCredential,
  profileToDraft,
} from "./llmRuntimeState";
import {
  buildOnboardingPayload,
  firstIncompleteTab,
  validateAdminMaintenance,
  validateClientAdminDraft,
} from "./onPremAdminState";
import "./OnPremAdmin.css";
import OnPremLlmRuntimePage from "./OnPremLlmRuntimePage";
import OnPremUnavailableState from "./OnPremUnavailableState";

const emptyClientDraft = {
  clientDisplayName: "",
  adminName: "",
  adminEmail: "",
  adminPassword: "",
  confirmPassword: "",
};

const defaultRuntimeSnapshot = {
  draft: { mode: "hosted", provider: "groq" },
  credential: { displayName: "", apiKey: "" },
  active: null,
};

function buildOnboardingLlmFromRuntime(snapshot = defaultRuntimeSnapshot) {
  const active = snapshot.active;
  const draft = active ? profileToDraft(active) : snapshot.draft || defaultRuntimeSnapshot.draft;
  const credential = active ? profileToCredential(active) : snapshot.credential || defaultRuntimeSnapshot.credential;
  const providerValue = active?.provider || draft.provider;
  const serverType = active?.server_type || draft.serviceType;
  const mode = active ? getRuntimeMode(active) : draft.mode;
  const provider =
    mode === "local"
      ? "vllm"
      : providerValue === "anthropic"
        ? "claude"
        : providerValue || "groq";
  return {
    provider,
    groqName: provider === "groq" ? credential.displayName : "",
    groqApiKey: provider === "groq" ? credential.apiKey : "",
    serverType,
  };
}

function isRuntimeReadyForOnboarding(active) {
  if (!active) return false;
  if (getRuntimeMode(active) === "local") return true;
  return Boolean(active.credential?.configured);
}

function getRuntimeReadyLabel(active) {
  if (!active) return "Create and activate a runtime config to complete onboarding.";
  if (!isRuntimeReadyForOnboarding(active)) {
    return `${getProviderLabel(active.provider, active.server_type)} is active but credential details are missing.`;
  }
  return `${getProviderLabel(active.provider, active.server_type)} is active and ready.`;
}

function FieldError({ id, children }) {
  return children ? <span id={id} className="onprem-field-error">{children}</span> : null;
}

function PasswordChecklist({ password }) {
  return (
    <div className="onprem-password-policy" aria-label="Password requirements">
      {getPasswordValidationState(password).map((rule) => (
        <span key={rule.key} className={rule.isValid ? "is-valid" : ""}>
          {rule.isValid ? <Check size={13} /> : <span className="onprem-rule-dot" />}
          {rule.label}
        </span>
      ))}
    </div>
  );
}

function Required() {
  return <span className="onprem-required" aria-hidden="true">*</span>;
}

function SecretInput({
  id,
  label,
  value,
  onChange,
  error,
  autoComplete = "new-password",
  placeholder = "",
  required = true,
}) {
  const [visible, setVisible] = useState(false);
  return (
    <label className="onprem-field" htmlFor={id}>
      <span>{label}{required ? <Required /> : null}</span>
      <div className="onprem-secret-input">
        <input
          id={id}
          className={error ? "is-error" : ""}
          type={visible ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          aria-label={label}
          autoComplete={autoComplete}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <button type="button" onClick={() => setVisible((current) => !current)} aria-label={`${visible ? "Hide" : "Show"} ${label.toLowerCase()}`}>
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </label>
  );
}

function SurfaceState({ title, message, onRetry, tone = "error" }) {
  return (
    <div className={`onprem-state is-${tone}`} role="status">
      <AlertCircle size={24} />
      <div><strong>{title}</strong><p>{message}</p></div>
      {onRetry ? (
        <button type="button" className="onprem-button is-secondary" onClick={onRetry}>
          <RefreshCcw size={15} /> Retry status
        </button>
      ) : null}
    </div>
  );
}

function TabBar({ activeTab, setActiveTab, llmDisabled = false }) {
  return (
    <div className="onprem-tabs" role="tablist" aria-label="Onboarding sections">
      <button type="button" role="tab" aria-selected={activeTab === "client-admin"} className={activeTab === "client-admin" ? "is-active" : ""} onClick={() => setActiveTab("client-admin")}>Client &amp; Admin</button>
      <button type="button" role="tab" aria-selected={activeTab === "llm"} className={activeTab === "llm" ? "is-active" : ""} disabled={llmDisabled} onClick={() => setActiveTab("llm")}>LLM Configuration</button>
    </div>
  );
}

function InitialOnboarding({ status, onStatusChange, onBlocked }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTabState] = useState(requestedTab === "llm" ? "llm" : "client-admin");
  const [client, setClient] = useState(emptyClientDraft);
  const [runtimeSnapshot, setRuntimeSnapshot] = useState(defaultRuntimeSnapshot);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const errorRef = useRef(null);
  const clientErrors = useMemo(() => validateClientAdminDraft(client), [client]);
  const clientReady = Object.keys(clientErrors).length === 0;
  const passwordsMatch = Boolean(client.confirmPassword) && client.adminPassword === client.confirmPassword;

  useEffect(() => {
    if (activeTab === "llm" && !clientReady) setActiveTabState("client-admin");
  }, [activeTab, clientReady]);

  const setActiveTab = (tab) => {
    if (tab === "llm" && !clientReady) return;
    setActiveTabState(tab);
    setSearchParams({ tab }, { replace: true });
  };

  const updateClient = (field) => (event) => {
    setClient((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setSubmitError("");
  };
  const handleRuntimeChange = useCallback((snapshot) => {
    setRuntimeSnapshot(snapshot);
    setSubmitError("");
  }, []);

  const continueToLlm = () => {
    const nextErrors = validateClientAdminDraft(client);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setSubmitError("Correct the highlighted fields before continuing.");
      requestAnimationFrame(() => errorRef.current?.focus());
      return;
    }
    setActiveTab("llm");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const onboardingLlm = buildOnboardingLlmFromRuntime(runtimeSnapshot);
    const runtimeReady = isRuntimeReadyForOnboarding(runtimeSnapshot.active);
    const nextErrors = validateClientAdminDraft(client);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setSubmitError("Correct the highlighted fields before submitting onboarding.");
      requestAnimationFrame(() => errorRef.current?.focus());
      return;
    }
    if (!runtimeReady) {
      setSubmitError("Activate a configured LLM runtime before completing onboarding.");
      requestAnimationFrame(() => errorRef.current?.focus());
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const { data } = await createOnPremOnboarding(buildOnboardingPayload(client, onboardingLlm));
      setClient(emptyClientDraft);
      onStatusChange(data);
      toast.success("On-prem onboarding saved.");
    } catch (error) {
      const responseStatus = error?.response?.status;
      const message = getOnPremErrorMessage(error, "Onboarding could not be completed.");
      setClient((current) => ({ ...current, adminPassword: "", confirmPassword: "" }));
      if (responseStatus === 409 || !error?.response) {
        try {
          const { data } = await getOnPremOnboarding();
          if (data?.first_admin) onStatusChange(data);
          else if (responseStatus === 409) onBlocked(message);
          else setSubmitError(`${message} Status was reloaded; re-enter the secret fields to try again.`);
        } catch {
          setSubmitError(`${message} Current onboarding status could not be confirmed.`);
        }
      } else {
        if (responseStatus === 422) setErrors(getOnPremValidationErrors(error));
        setSubmitError(message);
      }
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setSubmitting(false);
    }
  };

  const runtimeReady = isRuntimeReadyForOnboarding(runtimeSnapshot.active);

  return (
    <>
      <TabBar activeTab={activeTab} setActiveTab={setActiveTab} llmDisabled={!clientReady} />
      {submitError ? <div ref={errorRef} tabIndex={-1} className="onprem-error-summary" role="alert">{submitError}</div> : null}
      {activeTab === "client-admin" ? (
        <section className="onprem-panel" role="tabpanel">
          <div className="onprem-section-head"><div><h2>Client &amp; Admin</h2><p>Set the installation identity and its single tenant administrator.</p></div><span className="onprem-step">Step 1 of 2</span></div>
          <div className="onprem-form-grid">
            <label className="onprem-field is-wide" htmlFor="client-display-name"><span>Client display name<Required /></span><input id="client-display-name" aria-label="Client display name" value={client.clientDisplayName} onChange={updateClient("clientDisplayName")} placeholder="Client company" maxLength={101} aria-invalid={Boolean(errors.clientDisplayName)} /><FieldError>{errors.clientDisplayName}</FieldError></label>
            <label className="onprem-field" htmlFor="admin-name"><span>Admin name<Required /></span><input id="admin-name" aria-label="Admin name" value={client.adminName} onChange={updateClient("adminName")} placeholder="Tenant admin" maxLength={101} aria-invalid={Boolean(errors.adminName)} /><FieldError>{errors.adminName}</FieldError></label>
            <label className="onprem-field" htmlFor="admin-email"><span>Admin email<Required /></span><input id="admin-email" aria-label="Admin email" type="email" value={client.adminEmail} onChange={updateClient("adminEmail")} placeholder="admin@client.example.com" maxLength={101} aria-invalid={Boolean(errors.adminEmail)} /><FieldError>{errors.adminEmail}</FieldError></label>
            <div className="is-wide"><div className="onprem-form-grid"><SecretInput id="admin-password" label="Admin password" value={client.adminPassword} onChange={updateClient("adminPassword")} error={errors.adminPassword} placeholder="Temporary password" /><SecretInput id="confirm-admin-password" label="Confirm admin password" value={client.confirmPassword} onChange={updateClient("confirmPassword")} error={errors.confirmPassword} placeholder="Re-enter password" /></div>{client.confirmPassword ? <div className={`onprem-password-match ${passwordsMatch ? "is-valid" : "is-invalid"}`}>{passwordsMatch ? "Passwords match" : "Passwords do not match"}</div> : null}<PasswordChecklist password={client.adminPassword} /></div>
          </div>
          <div className="onprem-actions"><button type="button" className="onprem-button" onClick={continueToLlm}>Continue to LLM configuration</button></div>
        </section>
      ) : (
        <OnPremLlmRuntimePage
          heading="Runtime profile draft"
          description="Create a draft, save credentials, then activate it when model settings are ready."
          headerMeta={<span className="onprem-step">Step 2 of 2</span>}
          onRuntimeChange={handleRuntimeChange}
          footer={
            <form className="onprem-runtime-onboarding-actions" onSubmit={submit}>
              <span className={`onprem-runtime-ready ${runtimeReady ? "is-ready" : "is-incomplete"}`}>
                {getRuntimeReadyLabel(runtimeSnapshot.active)}
              </span>
              <button type="button" className="onprem-button is-secondary" onClick={() => setActiveTab("client-admin")}>
                Back
              </button>
              <button type="submit" className="onprem-button" disabled={submitting || !runtimeReady}>
                {submitting ? <Loader2 className="platform-spin" size={15} /> : <CheckCircle2 size={15} />}
                Complete onboarding
              </button>
            </form>
          }
        />
      )}
    </>
  );
}

function Maintenance({ status, onStatusChange }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabState] = useState(searchParams.get("tab") === "llm" ? "llm" : "client-admin");
  const [clientName, setClientName] = useState(status.client_display_name || "");
  const [admin, setAdmin] = useState({ name: status.first_admin?.name || "", email: status.first_admin?.email || "", password: "", confirmPassword: "" });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const adminPasswordsMatch = Boolean(admin.confirmPassword) && admin.password === admin.confirmPassword;
  const setActiveTab = (tab) => { setActiveTabState(tab); setSearchParams({ tab }, { replace: true }); setMessage(""); setErrors({}); };

  const saveClient = async (event) => {
    event.preventDefault();
    const value = clientName.trim();
    if (!value || value.length > 100) return setErrors({ clientName: "Enter a client name using 100 characters or fewer." });
    setBusy("client"); setErrors({}); setMessage("");
    try { const { data } = await updateOnPremClient({ client_display_name: value }); onStatusChange(data); setMessage("Client display name updated."); }
    catch (error) { setMessage(getOnPremErrorMessage(error, "Client display name could not be updated.")); }
    finally { setBusy(""); }
  };

  const saveAdmin = async (event) => {
    event.preventDefault();
    const result = validateAdminMaintenance(admin, status.first_admin);
    if (Object.keys(result.errors).length) return setErrors(result.errors);
    if (!result.changed) return setMessage("No admin changes to save.");
    const payload = {};
    if (admin.name.trim() !== status.first_admin.name) payload.name = admin.name.trim();
    if (admin.email.trim() !== status.first_admin.email) payload.email = admin.email.trim();
    if (admin.password) payload.password = admin.password;
    setBusy("admin"); setErrors({}); setMessage("");
    try { const { data } = await updateOnPremFirstAdmin(payload); onStatusChange(data); setAdmin((current) => ({ ...current, password: "", confirmPassword: "" })); setMessage(payload.email || payload.password ? "Admin updated. Existing tenant-admin sessions have been invalidated." : "Admin updated."); }
    catch (error) { setAdmin((current) => ({ ...current, password: "", confirmPassword: "" })); setMessage(getOnPremErrorMessage(error, "Admin could not be updated.")); }
    finally { setBusy(""); }
  };

  const configured = status.onboarding_complete;
  return (
    <>
      <div className={`onprem-health ${configured ? "is-ready" : "is-incomplete"}`}><CheckCircle2 size={18} /><div><strong>{configured ? "Onboarding complete" : "Onboarding incomplete"}</strong><span>{configured ? "Client, administrator, and LLM are operational." : "The selected LLM provider is not configured."}</span></div></div>
      <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
      {message ? <div className="onprem-message" role="status">{message}</div> : null}
      {activeTab === "client-admin" ? <div className="onprem-maintenance-grid">
        <form className="onprem-panel" onSubmit={saveClient}><div className="onprem-section-head"><div><h2>Client</h2><p>Update the display name shown to tenant users.</p></div></div><label className="onprem-field" htmlFor="maintenance-client-name"><span>Client display name<Required /></span><input id="maintenance-client-name" aria-label="Client display name" value={clientName} onChange={(event) => setClientName(event.target.value)} placeholder="Client company" /><FieldError>{errors.clientName}</FieldError></label><div className="onprem-actions"><button className="onprem-button" disabled={busy === "client" || clientName.trim() === status.client_display_name}><Save size={15} /> Save client</button></div></form>
        <form className="onprem-panel" onSubmit={saveAdmin}><div className="onprem-section-head"><div><h2>First administrator</h2><p>Changing email or password signs this administrator out of existing sessions.</p></div></div><div className="onprem-readonly-row"><span>Role <strong>{status.first_admin.role}</strong></span><span>State <strong>{String(status.first_admin.status) === "1" ? "Active" : status.first_admin.status}</strong></span></div><div className="onprem-form-grid"><label className="onprem-field" htmlFor="maintenance-admin-name"><span>Admin name<Required /></span><input id="maintenance-admin-name" aria-label="Admin name" value={admin.name} onChange={(event) => setAdmin((current) => ({ ...current, name: event.target.value }))} placeholder="Tenant admin" /><FieldError>{errors.name}</FieldError></label><label className="onprem-field" htmlFor="maintenance-admin-email"><span>Admin email<Required /></span><input id="maintenance-admin-email" aria-label="Admin email" type="email" value={admin.email} onChange={(event) => setAdmin((current) => ({ ...current, email: event.target.value }))} placeholder="admin@client.example.com" /><FieldError>{errors.email}</FieldError></label><SecretInput id="maintenance-admin-password" label="New password" value={admin.password} onChange={(event) => setAdmin((current) => ({ ...current, password: event.target.value }))} error={errors.password} placeholder="Temporary password" required={false} /><SecretInput id="maintenance-confirm-password" label="Confirm new password" value={admin.confirmPassword} onChange={(event) => setAdmin((current) => ({ ...current, confirmPassword: event.target.value }))} error={errors.confirmPassword} placeholder="Re-enter password" required={false} /></div>{admin.confirmPassword ? <div className={`onprem-password-match ${adminPasswordsMatch ? "is-valid" : "is-invalid"}`}>{adminPasswordsMatch ? "Passwords match" : "Passwords do not match"}</div> : null}{admin.password ? <PasswordChecklist password={admin.password} /> : null}<div className="onprem-actions"><button className="onprem-button" disabled={busy === "admin"}><Save size={15} /> Save administrator</button></div></form>
      </div> : <OnPremLlmRuntimePage />}
    </>
  );
}

export default function OnPremOnboardingPage() {
  const [state, setState] = useState({ status: "loading", data: null, message: "" });
  const loadStatus = useCallback(async () => {
    setState((current) => ({ ...current, status: "loading", message: "" }));
    try { const { data } = await getOnPremOnboarding(); setState({ status: "ready", data, message: "" }); }
    catch (error) {
      const code = error?.response?.status;
      setState({ status: code === 403 ? "denied" : code === 404 ? "unavailable" : "error", data: null, message: getOnPremErrorMessage(error, "Onboarding status could not be loaded.") });
    }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  return <div className="onprem-page"><div className="onprem-page-heading"><div><h1>Onboarding</h1><p>Configure the client, first administrator, and installation LLM provider.</p></div></div>{state.status === "loading" ? <div className="onprem-loading"><Loader2 className="platform-spin" size={22} /> Loading onboarding status...</div> : null}{state.status === "error" ? <SurfaceState title="Could not load onboarding" message={state.message} onRetry={loadStatus} /> : null}{state.status === "denied" ? <SurfaceState title="Access denied" message={state.message} /> : null}{state.status === "unavailable" ? <OnPremUnavailableState message={state.message} /> : null}{state.status === "blocked" ? <SurfaceState title="Onboarding is blocked" message={state.message} onRetry={loadStatus} /> : null}{state.status === "ready" && state.data ? (state.data.first_admin ? <Maintenance status={state.data} onStatusChange={(data) => setState({ status: "ready", data, message: "" })} /> : <InitialOnboarding status={state.data} onStatusChange={(data) => setState({ status: "ready", data, message: "" })} onBlocked={(message) => setState({ status: "blocked", data: null, message })} defaultTab={firstIncompleteTab(state.data)} />) : null}</div>;
}
